/**
 * Are you recommended (SYR-111, the rank question).
 *
 * Sixty times per model with search and sixty from memory, the models were
 * asked which six brands they would consider, in order, with the brand never
 * named. This section is the answer: how often the brand is named first, how
 * often it is in the six, who sits ahead of it, and who else came up.
 */

import React from 'react';
import { Table } from 'antd';
import { Card, Eyebrow } from '../../atoms';
import { THEME } from '../../config/theme';
import { IModelBeliefsGateSummary } from '../../../../services/nestjs-base-api';
import { InfoTip } from './InfoTip';
import { ReadSection } from './ReadSection';

const pct = (fraction: number | null | undefined): string =>
  fraction === null || fraction === undefined ? '–' : `${Math.round(fraction * 1000) / 10}%`;
const position = (value: number | null | undefined): string =>
  value === null || value === undefined ? '–' : value.toFixed(2);
const score = (value: number | null | undefined): string =>
  value === null || value === undefined ? '–' : value.toFixed(2);

interface Props {
  gate: IModelBeliefsGateSummary | null;
  note?: string;
  panel?: { label: string; provider: string; model: string }[];
  brandName?: string;
  // The section's number; it follows the fixes on the two-tier read.
  n?: string;
}

const joinNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

// One cell of the stat strip, the Briefing's `.snap-cell`.
const Stat: React.FunctionComponent<{ label: React.ReactNode; value: string; caption: React.ReactNode; first?: boolean }> = ({
  label,
  value,
  caption,
  first,
}) => (
  <div
    style={{
      padding: '4px 22px',
      borderLeft: first ? 'none' : `1px solid ${THEME.borderSubtle}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }}
  >
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: THEME.textMuted }}>
      {label}
    </span>
    <span style={{ fontSize: 38, fontWeight: 800, color: THEME.textBase, lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: 12.5, color: THEME.textSecondary, lineHeight: 1.4 }}>{caption}</span>
  </div>
);

export const GateSummary: React.FunctionComponent<Props> = ({ gate, note, panel, brandName, n = '1' }) => {
  const labelFor = (provider?: string, model?: string): string => {
    const match = (panel ?? []).find((m) => m.provider === provider && m.model === model);
    return match?.label ?? model ?? '–';
  };
  const searched = (gate?.byMode ?? []).find((mode) => mode.retrievalMode === 'on');
  const memory = (gate?.byMode ?? []).find((mode) => mode.retrievalMode === 'off');
  const method = gate?.method ?? null;
  const who = method?.models?.length ? joinNames(method.models) : 'ChatGPT, Claude and Gemini';
  const you = brandName ?? 'your brand';

  const sub = !gate || !searched ? (
    'The recommendation question did not run for this read. It needs a category to ask about.'
  ) : method ? (
    <>
      The question, exactly as {who} were asked it: <strong style={{ color: THEME.textBase }}>“{method.question}”</strong>{' '}
      {method.totalAnswers} answers in all, half with live web search and half from memory, and {you} was never in the
      question. Position means where in the list of six a brand was named, 1 being first.
    </>
  ) : (
    'Each model was asked, as a shopper, which brands it would consider, many times with and without web search, with your brand never named in the question.'
  );

  const ranked = !!searched && searched.namedFirstRate !== undefined;
  const perModel = (gate?.byCell ?? []).filter((cell) => cell.retrievalMode === 'on');
  // The API ranks the table; the fallback serves reads from before it did.
  const ordered = gate?.rankTable && ranked
    ? gate.rankTable.map((row) => ({
        ...row,
        name: row.isBrand ? brandName ?? 'You' : row.name,
      }))
    : gate && searched && ranked
    ? [
        {
          name: brandName ?? 'You',
          inclusionRate: searched.inclusionRate,
          meanPosition: searched.meanPosition,
          namedFirstRate: searched.namedFirstRate ?? null,
          rankScore: searched.rankScore ?? null,
          isBrand: true,
        },
        ...(gate.competitors ?? []).map((c) => ({ ...c, isBrand: false })),
      ].sort((a: any, b: any) => (b.rankScore ?? 0) - (a.rankScore ?? 0))
    : [];
  const inTable = new Set(ordered.map((row: any) => row.name));
  // Everyone else the models named, with the brands already in the table
  // left out so nobody is listed twice.
  const alsoNamed = (gate?.unknownBrands ?? []).filter((brand) => !inTable.has(brand.name));
  const searchedAnswers = gate?.method?.searchedAnswers ?? searched?.asked ?? 0;

  return (
    <ReadSection id="recommended" n={n} title="Would the models recommend you?" sub={sub}>
      {gate && searched && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, alignItems: 'start' }}>
          <Card>
            <Eyebrow style={{ marginBottom: 16 }}>With live web search</Eyebrow>
            <div style={{ display: 'grid', gridTemplateColumns: ranked ? '1fr 1fr' : '1fr', margin: '0 -22px 18px' }}>
              {ranked && (
                <Stat
                  label="Named first"
                  value={pct(searched.namedFirstRate)}
                  caption={method ? `of the ${method.searchedAnswers} answers with web search put ${you} first` : 'of the answers put you first'}
                  first
                />
              )}
              <Stat
                label={ranked ? 'In the six' : 'In consideration'}
                value={pct(searched.inclusionRate)}
                caption={
                  ranked
                    ? `listed ${you} at all, at position ${position(searched.meanPosition)} of six on average`
                    : `of shopper questions, average position ${position(searched.meanPosition)}`
                }
                first={!ranked}
              />
            </div>
            {memory && (
              <div
                style={{
                  borderTop: `1px solid ${THEME.borderSubtle}`,
                  paddingTop: 14,
                  fontSize: 13.5,
                  color: THEME.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: THEME.textBase }}>From memory, without searching:</strong>{' '}
                {ranked
                  ? `${pct(memory.namedFirstRate)} named ${you} first and ${pct(memory.inclusionRate)} listed it`
                  : `${pct(memory.inclusionRate)} listed ${you}, average position ${position(memory.meanPosition)}`}
                . {searched.inclusionRate > (memory.inclusionRate ?? 0) + 0.05
                  ? 'What the models find on the web is earning you recommendations their memory alone would not.'
                  : searched.inclusionRate < (memory.inclusionRate ?? 0) - 0.05
                    ? 'What the models find on the web is costing you recommendations their memory alone would give.'
                    : 'Searching the web changes little: the models already hold this view.'}
              </div>
            )}
            {note && (
              <p style={{ fontSize: 13.5, color: THEME.textBase, lineHeight: 1.55, margin: '16px 0 0' }}>{note}</p>
            )}
            {perModel.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 12, color: THEME.textSecondary, marginBottom: 8 }}>
                  The same answers, split by model. All were asked the same question the same number of times, with web search on.
                </div>
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(row: any) => `${row.provider}:${row.model}`}
                  dataSource={perModel}
                  columns={[
                    { title: 'Model', dataIndex: 'label', render: (_: unknown, row: any) => row.label ?? labelFor(row.provider, row.model) },
                    ...(ranked ? [{ title: 'Named first', dataIndex: 'namedFirstRate', render: (v: number) => pct(v) }] : []),
                    { title: ranked ? 'In the six' : 'In consideration', dataIndex: 'inclusionRate', render: (v: number) => pct(v) },
                    { title: 'Average position', dataIndex: 'meanPosition', render: (v: number | null) => position(v) },
                    ...(ranked
                      ? [
                          {
                            title: (
                              <InfoTip title="Named first = 7, second = 6, down to sixth = 2, and not in the six = 1, averaged over every answer. The same 1 to 7 scale as the statements below.">
                                Rank score
                              </InfoTip>
                            ),
                            dataIndex: 'rankScore',
                            render: (v: number | null | undefined) => score(v),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            )}
          </Card>

          <Card>
            <Eyebrow style={{ marginBottom: 6 }}>{ranked ? 'Who the models name, in order' : 'Listed instead'}</Eyebrow>
            {ranked ? (
              <>
                <div style={{ fontSize: 12, color: THEME.textSecondary, marginBottom: 14, lineHeight: 1.5 }}>
                  Your competitors and you, with any brand the models put ahead of you that is not in your set, from the
                  same answers with web search. The bar is the{' '}
                  <InfoTip title="Named first = 7, second = 6, down to sixth = 2, and not in the six = 1, averaged over every answer.">rank score</InfoTip>
                  , 7 for always first down to 1 for never listed. A brand that is in nearly every list outranks one
                  that is named first more often but left out more often.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {ordered.map((row: any) => {
                    const width = row.rankScore === null ? 0 : Math.max(0, Math.min(100, ((row.rankScore - 1) / 6) * 100));
                    return (
                      <div key={row.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                          <span style={{ fontSize: 13.5, fontWeight: row.isBrand ? 700 : 500, color: THEME.textBase }}>
                            {row.name}
                            {row.configured === false && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  color: THEME.textMuted,
                                  background: THEME.bgChip,
                                  borderRadius: 999,
                                  padding: '1px 7px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                not in your set
                              </span>
                            )}
                          </span>
                          <span style={{ fontSize: 12, color: THEME.textSecondary }}>
                            <strong style={{ color: THEME.textBase }}>{score(row.rankScore)}</strong>
                            {' · '}first {pct(row.namedFirstRate)} · listed {pct(row.inclusionRate)} · position{' '}
                            {position(row.meanPosition)} when listed
                          </span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, background: THEME.bgChip, overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${width}%`,
                              height: '100%',
                              borderRadius: 4,
                              background: row.isBrand ? THEME.primary : '#94A3B8',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey="name"
                dataSource={gate.competitors ?? []}
                columns={[
                  { title: 'Brand', dataIndex: 'name' },
                  { title: 'In consideration', dataIndex: 'inclusionRate', render: (v: number) => pct(v) },
                  { title: 'Average position', dataIndex: 'meanPosition', render: (v: number | null) => position(v) },
                  {
                    title: 'In the models’ words',
                    dataIndex: 'reasons',
                    render: (reasons: string[] | undefined) => (reasons?.length ? `“${reasons[0]}”` : '–'),
                  },
                ]}
              />
            )}

            {alsoNamed.length > 0 && (
              <div style={{ marginTop: 20, borderTop: `1px solid ${THEME.borderSubtle}`, paddingTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: THEME.textBase, marginBottom: 8 }}>
                  <InfoTip
                    title={
                      searchedAnswers
                        ? `The number beside each brand is how many of the ${searchedAnswers} answers with web search listed it.`
                        : 'The number beside each brand is how many answers with web search listed it.'
                    }
                  >
                    Also named
                  </InfoTip>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {alsoNamed.slice(0, 12).map((brand) => (
                    <span
                      key={brand.name}
                      style={{
                        fontSize: 12,
                        color: THEME.textSecondary,
                        background: THEME.bgChip,
                        border: `1px solid ${THEME.border}`,
                        borderRadius: 999,
                        padding: '3px 10px',
                      }}
                    >
                      {brand.name} · {brand.count}
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: THEME.textMuted, marginTop: 8 }}>
                  Other brands that came up in {searchedAnswers ? `the ${searchedAnswers} answers with web search` : 'the answers'},
                  with the number of answers each appeared in. They are not in your competitor set and none of them outranks
                  you; the models brought them up on their own.
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </ReadSection>
  );
};
